import { useEffect, useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import {fetchWithAuth} from '../libs/apiUtils'
import {useNavigate} from 'react-router-dom'
import {Meta} from '../types/global'
import {PencilIcon, TrashIcon, PlusIcon, TableCellsIcon, LockClosedIcon, StarIcon} from '@heroicons/react/24/outline'
import DeleteGroupModal from '../components/group/DeleteGroupModal'
import EditGroupModal from '../components/group/EditGroupModal'
import CreateGroupModal from '../components/group/CreateGroupModal'
import Pagination from '../components/Pagination'

interface GroupData {
  id: string
  name: string
  isDefault?: boolean
  tableCount?: number
  createdByName?: string
}

type TabType = 'custom' | 'default'

export default function Group() {
  const [groups, setGroups] = useState<GroupData[]>([])
  const [meta, setMeta] = useState<Meta | null>(null)
  const [page, setPage] = useState<number>(1)
  const [perPage, setPerPage] = useState<number>(10)
  const [loading, setLoading] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<TabType>('custom')
  const navigate = useNavigate()

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [toBeDeletedGroup, setToBeDeletedGroup] = useState<{id?: string, name?: string}>({})

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [toBeEditedGroup, setToBeEditedGroup] = useState<{id?: string, name?: string, isDefault?: boolean}>({})

  const userRoleSlug = localStorage.getItem('user_role_slug')
  const isSuperAdmin = userRoleSlug === 'super_admin'
  const isConsumer = userRoleSlug === 'consumer'
  const allowedUsers = ['admin', 'super_admin']
  const doesUserHaveAccess = allowedUsers.includes(userRoleSlug ?? '')

  const loadGroups = useCallback(async (currentPage = 1) => {
    try {
      setLoading(true)
      const isDefault = activeTab === 'default'
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: currentPage, perPage, isDefault }),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data.message || 'Error fetching group data')
        return
      }

      setGroups(data.data || [])
      setMeta(data.meta)
    } catch (err) {
      toast.error('Network error, check your connection')
    } finally {
      setLoading(false)
    }
  }, [activeTab, perPage])

  useEffect(() => {
    setPage(1)
    loadGroups(1)
  }, [activeTab, perPage, loadGroups])

  useEffect(() => {
    loadGroups(page)
  }, [page, loadGroups])

  async function handleDeleteGroup(id: string) {
    try {
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/group/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data.message || 'Error deleting group')
        return
      }

      toast.success('Group deleted successfully')
      loadGroups(page)
    } catch (err) {
      toast.error('Network error, check your connection')
    }
  }

  const canManage = activeTab === 'custom' ? doesUserHaveAccess : isSuperAdmin

  const tabs: { key: TabType; label: string; icon: React.ReactNode }[] = [
    { key: 'custom', label: 'Grup Kustom', icon: <TableCellsIcon className="size-4" /> },
    { key: 'default', label: 'Grup Bawaan', icon: <StarIcon className="size-4" /> },
  ]

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-3xl font-extrabold text-neutral-900 tracking-tight">
            {!doesUserHaveAccess ? 'Akses Grup Data' : 'Manajemen Grup'}
          </h2>
          <p className="mt-1 text-sm text-neutral-500">
            {!doesUserHaveAccess ? 'Jelajahi grup data yang tersedia untuk Anda.' : 'Kelola daftar grup data yang tersedia dalam sistem.'}
          </p>
        </div>
        
        { canManage && (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg shadow-primary-200 transition-all active:scale-95"
          >
            <PlusIcon className="size-5" />
            {activeTab === 'default' ? 'Tambah Grup Bawaan' : 'Tambah Grup'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-neutral-100 rounded-xl mb-6 w-fit">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === tab.key
                ? 'bg-white text-primary-700 shadow-sm'
                : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            {tab.icon}
            {tab.label}
            {activeTab === tab.key && tab.key === 'default' && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded font-medium">
                Super Admin
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Default tab info banner for admins */}
      {activeTab === 'default' && !isSuperAdmin && !isConsumer && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl mb-4 text-sm">
          <LockClosedIcon className="size-5 shrink-0 text-amber-600" />
          <span>Grup bawaan ini dikelola oleh Super Admin. Anda hanya dapat melihat dan memasukkan data di kategorinya.</span>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-xl shadow-neutral-100 border border-neutral-100 overflow-hidden">
        { loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-neutral-500 font-medium">Memuat data...</p>
          </div>
        ) : groups.length === 0 ? (
          <div className="p-20 text-center">
            <div className="bg-primary-50 size-16 rounded-full flex items-center justify-center mx-auto mb-4 text-primary-600">
              {activeTab === 'default' ? <StarIcon className="size-8" /> : canManage ? <PlusIcon className="size-8" /> : <TableCellsIcon className="size-8" />}
            </div>
            <h3 className="text-lg font-bold text-neutral-800">
              {activeTab === 'default' ? 'Belum Ada Grup Bawaan' : canManage ? 'Belum Ada Grup' : 'Tidak Ada Akses Grup'}
            </h3>
            <p className="text-neutral-500 max-w-xs mx-auto mt-2">
              {activeTab === 'default'
                ? (isSuperAdmin ? 'Klik "Tambah Grup Bawaan" untuk membuat grup yang berlaku untuk semua admin.' : 'Belum ada grup bawaan yang dibuat oleh Super Admin.')
                : canManage
                  ? 'Silakan klik tombol "Tambah Grup" untuk memulai pengelolaan data.'
                  : 'Anda belum diberikan izin untuk mengakses grup data manapun. Silakan hubungi admin Anda.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-neutral-50/50">
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider w-20">No</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider">Nama Grup</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider">Dibuat Oleh</th>
                  { canManage &&
                    <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider w-32 text-center">Aksi</th>
                  }
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {groups.map((group, index) => (
                  <tr 
                    key={group.id} 
                    className="group hover:bg-primary-50/30 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-neutral-500">
                      {(meta?.perPage || 20) * (page - 1) + index + 1}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {group.isDefault && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                            <StarIcon className="size-3" /> Bawaan
                          </span>
                        )}
                        <button
                          className="text-neutral-900 font-semibold hover:text-primary-600 transition-colors text-left"
                          onClick={() => navigate(`/data/${group.id}/category`)}
                        >
                          {group.name}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-neutral-500">{group.createdByName || '-'}</span>
                    </td>
                    { canManage && (
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            title="Edit Grup"
                            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                            onClick={() => {
                              setToBeEditedGroup({id: group.id, name: group.name, isDefault: group.isDefault})
                              setIsEditModalOpen(true)
                            }}
                          >
                            <PencilIcon className="size-5" />
                          </button>
                          <button
                            title="Hapus Grup"
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            onClick={() => {
                              setToBeDeletedGroup({id: group.id, name: group.name})
                              setIsDeleteModalOpen(true)
                            }}
                          >
                            <TrashIcon className="size-5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {meta && (
          <div className="px-6 py-4 bg-neutral-50/50 border-t border-neutral-100">
            <Pagination
              currentPage={page}
              lastPage={meta.lastPage}
              total={meta.total}
              perPage={perPage}
              onPageChange={setPage}
              onPerPageChange={(newPerPage) => {
                setPerPage(newPerPage)
                setPage(1)
              }}
            />
          </div>
        )}
      </div>

      <CreateGroupModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        onSuccess={() => loadGroups(page)}
        defaultMode={activeTab === 'default'}
      />
      <DeleteGroupModal 
        isOpen={isDeleteModalOpen} 
        setIsOpen={setIsDeleteModalOpen} 
        name={toBeDeletedGroup.name ?? ''}
        onSubmit={() => handleDeleteGroup(toBeDeletedGroup.id ?? '')}
      />
      <EditGroupModal 
        isOpen={isEditModalOpen}
        setIsOpen={setIsEditModalOpen}
        group={toBeEditedGroup}
        onSuccess={() => loadGroups(page)}
        isSuperAdmin={isSuperAdmin}
      />
    </div>
  )
}
